import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Pencil, RotateCcw, XCircle } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import TypeToConfirmDialog from '../../../components/TypeToConfirmDialog';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import AdminModal from '../../components/AdminModal';
import FieldList from '../../components/FieldList';
import AllFieldsSection from '../../components/AllFieldsSection';
import EntityAuditList from '../../components/EntityAuditList';
import StatusBadge from '../../components/StatusBadge';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
} from '../../../components/StandardTable';
import { formatDate, formatDateTime } from '../../lib/format';
import OrgMembersTab from './OrgMembersTab';
import OrgWorkspaceCollaborators from './OrgWorkspaceCollaborators';
import { tierLabel, useOrgFilterOptions } from '../../../components/admin/useOrgFilterOptions';

interface OrgDetail {
  organization: Record<string, unknown> & {
    id: string;
    name: string;
    org_category?: string | null;
    subscription?: string | null;
    description?: string | null;
    email?: string | null;
    website?: string | null;
    phone?: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  member_counts: {
    total: number;
    staff: number;
    citizen: number;
    org_admins: number;
  };
  workspaces: Array<{
    id: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    member_count: string;
    bill_count: string;
    owners: string | null;
    last_activity_at: string | null;
  }>;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'members', label: 'Members' },
  { key: 'workspaces', label: 'Workspaces' },
  { key: 'activity', label: 'Activity' },
];

const AccountOrganizationDetailPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', subscription: '', description: '', email: '', website: '', phone: '' });
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [impact, setImpact] = useState<{ activeUsers: number; activeWorkspaces: number; totalWorkspaces: number } | null>(null);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [workspaceLifecycleTarget, setWorkspaceLifecycleTarget] = useState<{
    id: string;
    name: string;
    action: 'deactivate' | 'reactivate';
  } | null>(null);

  const detail = useQuery({
    queryKey: ['admin-accounts', 'organization', orgId],
    queryFn: async () => {
      const response = await api.get(`/admin/accounts/organizations/${orgId}`);
      return response.data.data as OrgDetail;
    },
    enabled: !!orgId,
  });

  // Subscription tiers from the authoritative subscription_types catalog
  // Always loaded (cached 5 min): the Subscription field shows the tier's label.
  const subscriptionTiers = useOrgFilterOptions();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'organization', orgId] });
    queryClient.invalidateQueries({ queryKey: ['admin-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'organization', orgId ?? ''] });
  };

  const editMutation = useMutation({
    mutationFn: () =>
      api.put(`/admin/organizations/${orgId}`, {
        name: editForm.name,
        subscription: editForm.subscription || undefined,
        description: editForm.description || undefined,
        email: editForm.email || undefined,
        website: editForm.website || undefined,
        phone: editForm.phone || undefined,
      }),
    onSuccess: () => {
      toast.success('Organization updated');
      setEditOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to update organization'),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.put(`/admin/accounts/organizations/${orgId}/deactivate`),
    onSuccess: () => {
      toast.success('Organization deactivated');
      setDeactivateOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to deactivate organization'),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.put(`/admin/accounts/organizations/${orgId}/reactivate`),
    onSuccess: () => {
      toast.success('Organization reactivated');
      setReactivateOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to reactivate organization'),
  });

  const workspaceLifecycleMutation = useMutation({
    mutationFn: ({ id: workspaceId, action }: { id: string; action: 'deactivate' | 'reactivate' }) =>
      api.put(`/admin/workspaces/${workspaceId}/${action}`),
    onSuccess: (_response, variables) => {
      toast.success(variables.action === 'deactivate' ? 'Workspace deactivated' : 'Workspace reactivated');
      setWorkspaceLifecycleTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Workspace update failed'),
  });

  if (detail.isLoading) {
    return (
      <AdminPageShell title="Organization" backUrl="/admin-next/accounts?tab=organizations">
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading organization…</p>
      </AdminPageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AdminPageShell title="Organization" backUrl="/admin-next/accounts?tab=organizations">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load this organization.
          </p>
          <Button variant="outline" size="sm" onClick={() => detail.refetch()}>
            Retry
          </Button>
        </div>
      </AdminPageShell>
    );
  }

  const { organization, member_counts: memberCounts, workspaces } = detail.data;

  const openEdit = () => {
    setEditForm({
      name: organization.name ?? '',
      subscription: (organization.subscription as string) ?? '',
      description: (organization.description as string) ?? '',
      email: (organization.email as string) ?? '',
      website: (organization.website as string) ?? '',
      phone: (organization.phone as string) ?? '',
    });
    setEditOpen(true);
  };

  const openDeactivate = async () => {
    setImpact(null);
    setDeactivateOpen(true);
    try {
      const response = await api.get(`/admin/accounts/organizations/${orgId}/impact`);
      setImpact(response.data.data);
    } catch {
      // dialog works without the preview
    }
  };

  return (
    <AdminPageShell
      title={organization.name}
      description="Organization"
      backUrl="/admin-next/accounts?tab=organizations"
      actions={
        <>
          <Button variant="outline" size="sm" icon={Pencil} onClick={openEdit}>
            Edit
          </Button>
          {organization.is_active ? (
            <Button variant="outline" size="sm" icon={XCircle} onClick={openDeactivate}>
              Deactivate
            </Button>
          ) : (
            <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setReactivateOpen(true)}>
              Reactivate
            </Button>
          )}
        </>
      }
    >
      <div className="flex items-center gap-2">
        <StatusBadge active={organization.is_active} />
        <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
          {memberCounts.staff} staff · {memberCounts.citizen} citizen{memberCounts.citizen === 1 ? '' : 's'} ·{' '}
          {workspaces.length} workspace{workspaces.length === 1 ? '' : 's'}
        </span>
      </div>

      <AdminTabs tabs={TABS} activeKey={tab} onChange={setTab} />

      {tab === 'overview' && (
        <div className="card space-y-4 p-5">
          <FieldList
            fields={[
              { label: 'ID', value: <span className="font-mono text-xs">{organization.id}</span>, copyValue: organization.id },
              { label: 'Name', value: organization.name },
              { label: 'Category', value: (organization.org_category as string) ?? '—' },
              { label: 'Subscription', value: organization.subscription ? tierLabel(subscriptionTiers.data, organization.subscription as string) : '—' },
              { label: 'Status', value: <StatusBadge active={organization.is_active} /> },
              { label: 'Created', value: formatDateTime(organization.created_at) },
              { label: 'Updated', value: formatDateTime(organization.updated_at) },
            ]}
          />
          <AllFieldsSection record={organization} />
        </div>
      )}

      {tab === 'members' && orgId && (
        <div className="space-y-8">
          <OrgMembersTab
            orgId={orgId}
            organizationName={organization.name}
            orgAdminCount={memberCounts.org_admins}
          />
          <OrgWorkspaceCollaborators orgId={orgId} workspaces={workspaces} />
        </div>
      )}

      {tab === 'workspaces' && (
        <StandardTable horizontalScroll>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell>Name</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Owner</StandardTableHeaderCell>
              <StandardTableHeaderCell align="right" columnType="fit">Members</StandardTableHeaderCell>
              <StandardTableHeaderCell align="right" columnType="fit" responsiveHide="lg">Bills</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" columnType="fit">Last Activity</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl" columnType="fit">Created</StandardTableHeaderCell>
              <StandardTableHeaderCell>Status</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {workspaces.length === 0 ? (
              <EmptyTableState colSpan={8} message="No workspaces" />
            ) : (
              workspaces.map((workspace) => (
                <StandardTableRow key={workspace.id} hoverable={false}>
                  <StandardTableCell>
                    <Link
                      to={`/admin-next/accounts/workspaces/${workspace.id}`}
                      className="font-medium text-primary-700 hover:underline dark:text-primary-400"
                    >
                      {workspace.name}
                    </Link>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{workspace.owners ?? '—'}</StandardTableCell>
                  <StandardTableCell align="right">{workspace.member_count}</StandardTableCell>
                  <StandardTableCell align="right" responsiveHide="lg">{workspace.bill_count}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {workspace.last_activity_at ? formatDateTime(workspace.last_activity_at) : '—'}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="xl">{formatDate(workspace.created_at)}</StandardTableCell>
                  <StandardTableCell>
                    <StatusBadge
                      active={workspace.is_active}
                      viaParent={workspace.is_active && !organization.is_active}
                    />
                  </StandardTableCell>
                  <StandardTableCell>
                    {workspace.is_active ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={XCircle}
                        onClick={() =>
                          setWorkspaceLifecycleTarget({ id: workspace.id, name: workspace.name, action: 'deactivate' })
                        }
                        ariaLabel={`Deactivate ${workspace.name}`}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={RotateCcw}
                        onClick={() =>
                          setWorkspaceLifecycleTarget({ id: workspace.id, name: workspace.name, action: 'reactivate' })
                        }
                        ariaLabel={`Reactivate ${workspace.name}`}
                      >
                        Reactivate
                      </Button>
                    )}
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {tab === 'activity' && orgId && <EntityAuditList entityType="organization" entityId={orgId} />}

      <AdminModal
        isOpen={editOpen}
        title="Edit Organization"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={editForm.name.trim().length === 0}
              loading={editMutation.isLoading}
              onClick={() => editMutation.mutate()}
            >
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="org-subscription" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Subscription
            </label>
            <select
              id="org-subscription"
              value={editForm.subscription}
              onChange={(event) => setEditForm((current) => ({ ...current, subscription: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            >
              {/* Preserve an unexpected current value so saving other fields doesn't change it */}
              {editForm.subscription &&
                !(subscriptionTiers.data?.subscriptions ?? []).includes(editForm.subscription) && (
                  <option value={editForm.subscription}>{tierLabel(subscriptionTiers.data, editForm.subscription)}</option>
                )}
              <option value="">(unchanged)</option>
              {(subscriptionTiers.data?.subscriptionTiers ?? []).map((tier) => (
                <option key={tier.key} value={tier.key}>
                  {tier.label}
                </option>
              ))}
            </select>
          </div>
          {(
            [
              { key: 'name', label: 'Name *', type: 'text' },
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'website', label: 'Website', type: 'text' },
              { key: 'phone', label: 'Phone', type: 'text' },
            ] as const
          ).map((field) => (
            <div key={field.key}>
              <label htmlFor={`org-${field.key}`} className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                {field.label}
              </label>
              <input
                id={`org-${field.key}`}
                type={field.type}
                value={editForm[field.key]}
                onChange={(event) => setEditForm((current) => ({ ...current, [field.key]: event.target.value }))}
                className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              />
            </div>
          ))}
          <div>
            <label htmlFor="org-description" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Description
            </label>
            <textarea
              id="org-description"
              rows={3}
              value={editForm.description}
              onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            />
          </div>
        </div>
      </AdminModal>

      <TypeToConfirmDialog
        isOpen={deactivateOpen}
        title="Deactivate organization"
        message={`Deactivating "${organization.name}" will suspend access for ${
          impact ? `${impact.activeUsers} active user${impact.activeUsers === 1 ? '' : 's'}` : 'its members'
        } and make ${
          impact ? `${impact.activeWorkspaces} of its ${impact.totalWorkspaces} workspace${impact.totalWorkspaces === 1 ? '' : 's'}` : 'its workspaces'
        } unavailable. Nothing is deleted; it can be reactivated later. Type the organization name to confirm.`}
        confirmationWord={organization.name}
        confirmText="Deactivate"
        onConfirm={() => deactivateMutation.mutate()}
        onCancel={() => setDeactivateOpen(false)}
        isLoading={deactivateMutation.isLoading}
      />

      <ConfirmationDialog
        isOpen={reactivateOpen}
        title="Reactivate organization"
        message={`Reactivate "${organization.name}"? Members regain access and its workspaces become available again, exactly as they were before deactivation.`}
        confirmText="Reactivate"
        variant="info"
        onConfirm={() => reactivateMutation.mutate()}
        onCancel={() => setReactivateOpen(false)}
      />

      <ConfirmationDialog
        isOpen={workspaceLifecycleTarget !== null}
        title={workspaceLifecycleTarget?.action === 'deactivate' ? 'Deactivate workspace' : 'Reactivate workspace'}
        message={
          workspaceLifecycleTarget
            ? workspaceLifecycleTarget.action === 'deactivate'
              ? `Deactivate "${workspaceLifecycleTarget.name}"? Members lose access until it is reactivated. Nothing is deleted.`
              : `Reactivate "${workspaceLifecycleTarget.name}"? Members regain access with their previous roles.`
            : ''
        }
        confirmText={workspaceLifecycleTarget?.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
        variant={workspaceLifecycleTarget?.action === 'deactivate' ? 'warning' : 'info'}
        onConfirm={() => workspaceLifecycleTarget && workspaceLifecycleMutation.mutate(workspaceLifecycleTarget)}
        onCancel={() => setWorkspaceLifecycleTarget(null)}
      />

    </AdminPageShell>
  );
};

export default AccountOrganizationDetailPage;
